import type {
    ClinicData,
    ClinicIndex,
    Consultation,
    FollowUp,
    FollowUpBucket,
    Patient,
    PatientSummary,
    Prescription
} from '@/lib/clinic/types'

const groupBy = <T>(items: T[], getKey: (item: T) => string | null): Map<string, T[]> => {
    const groups = new Map<string, T[]>()

    for (const item of items) {
        const key = getKey(item)
        if (!key) continue

        const group = groups.get(key)

        if (group) group.push(item)
        else groups.set(key, [item])
    }

    return groups
}

export const buildClinicIndex = (data: ClinicData): ClinicIndex => {
    const consultations = [...data.consultations].sort((a, b) => b.consultedAt.localeCompare(a.consultedAt))
    const prescriptions = [...data.prescriptions].sort((a, b) => b.prescribedAt.localeCompare(a.prescribedAt))
    const followUps = [...data.followUps].sort((a, b) => b.dueDate.localeCompare(a.dueDate) || b.createdAt.localeCompare(a.createdAt))
    const reminders = [...data.reminders].sort((a, b) => b.at.localeCompare(a.at))

    const consultationsByPatient = groupBy(consultations, item => item.patientId)
    const prescriptionsByPatient = groupBy(prescriptions, item => item.patientId)
    const followUpsByPatient = groupBy(followUps, item => item.patientId)

    const summaries = new Map<string, PatientSummary>()

    for (const patient of data.patients) {
        const patientConsultations = consultationsByPatient.get(patient.id) ?? []
        const pending = (followUpsByPatient.get(patient.id) ?? [])
            .filter(item => item.status === 'pending')
            .sort((a, b) => a.dueDate.localeCompare(b.dueDate))

        summaries.set(patient.id, {
            consultationCount: patientConsultations.length,
            lastConsultationAt: patientConsultations[0]?.consultedAt ?? null,
            prescriptionCount: prescriptionsByPatient.get(patient.id)?.length ?? 0,
            nextFollowUp: pending[0] ?? null
        })
    }

    return {
        patientsById: new Map(data.patients.map(item => [item.id, item])),
        patientsByCode: new Map(data.patients.map(item => [item.code.toLowerCase(), item])),
        consultationsById: new Map(data.consultations.map(item => [item.id, item])),
        prescriptionsById: new Map(data.prescriptions.map(item => [item.id, item])),
        followUpsById: new Map(data.followUps.map(item => [item.id, item])),
        consultationsByPatient,
        prescriptionsByPatient,
        prescriptionsByConsultation: groupBy(prescriptions, item => item.consultationId),
        followUpsByPatient,
        followUpsByConsultation: groupBy(followUps, item => item.consultationId),
        remindersByFollowUp: groupBy(reminders, item => item.followUpId),
        remindersByPatient: groupBy(reminders, item => item.patientId),
        summaries
    }
}

export const EMPTY_SUMMARY: PatientSummary = {
    consultationCount: 0,
    lastConsultationAt: null,
    prescriptionCount: 0,
    nextFollowUp: null
}

export const getFollowUpBucket = (followUp: FollowUp, today: string): FollowUpBucket => {
    if (followUp.status === 'completed') return 'completed'
    if (followUp.dueDate === today) return 'today'

    return followUp.dueDate < today ? 'overdue' : 'upcoming'
}

export const sortFollowUpsForBucket = (items: FollowUp[], bucket: FollowUpBucket): FollowUp[] => {
    const sorted = [...items]

    if (bucket === 'completed') {
        return sorted.sort((a, b) => (b.completedAt ?? b.updatedAt).localeCompare(a.completedAt ?? a.updatedAt))
    }

    return sorted.sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.createdAt.localeCompare(b.createdAt))
}

export type TimelineEvent =
    | { kind: 'registration', at: string, patient: Patient }
    | { kind: 'visit', at: string, consultation: Consultation, prescriptions: Prescription[], followUps: FollowUp[] }
    | { kind: 'prescription', at: string, prescription: Prescription }
    | { kind: 'follow-up', at: string, followUp: FollowUp }

export const buildPatientTimeline = (patient: Patient, index: ClinicIndex): TimelineEvent[] => {
    const consultations = index.consultationsByPatient.get(patient.id) ?? []
    const prescriptions = index.prescriptionsByPatient.get(patient.id) ?? []
    const followUps = index.followUpsByPatient.get(patient.id) ?? []

    const events: TimelineEvent[] = [{ kind: 'registration', at: patient.createdAt, patient }]

    for (const consultation of consultations) {
        events.push({
            kind: 'visit',
            at: consultation.consultedAt,
            consultation,
            prescriptions: [...(index.prescriptionsByConsultation.get(consultation.id) ?? [])].reverse(),
            followUps: [...(index.followUpsByConsultation.get(consultation.id) ?? [])].sort((a, b) => a.dueDate.localeCompare(b.dueDate))
        })
    }

    for (const prescription of prescriptions) {
        if (!prescription.consultationId || !index.consultationsById.has(prescription.consultationId)) {
            events.push({ kind: 'prescription', at: prescription.prescribedAt, prescription })
        }
    }

    for (const followUp of followUps) {
        if (!followUp.consultationId || !index.consultationsById.has(followUp.consultationId)) {
            events.push({ kind: 'follow-up', at: followUp.createdAt, followUp })
        }
    }

    return events.sort((a, b) => a.at.localeCompare(b.at))
}
